import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { GlobalRole } from '../types';
import { requiresOrganizationOnboarding } from '../lib/onboarding';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: GlobalRole[];
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, hasCheckedSession, fetchUser } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    if (!hasCheckedSession && !isLoading) {
      void fetchUser();
    }
  }, [fetchUser, hasCheckedSession, isLoading]);

  if (isLoading || !hasCheckedSession) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="text-lg text-gray-900 dark:text-gray-100">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(next)}`} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const roleDashboards: Record<GlobalRole, string> = {
      AGENCY: '/agency/dashboard',
      BRAND: '/brand/dashboard',
      KOL: '/kol/dashboard',
      ADMIN: '/admin/dashboard',
    };
    return <Navigate to={roleDashboards[user.role] || '/login'} replace />;
  }

  if (
    requiresOrganizationOnboarding(user) &&
    location.pathname !== '/onboarding/organization'
  ) {
    return <Navigate to="/onboarding/organization" replace />;
  }

  return <>{children}</>;
}

